import { fetcher } from '@/config/api';

export type ThemePreference = 'light' | 'dark';

export interface UserSettings {
  id: string;
  name?: string;
  email?: string;
  user_principal_name?: string;
  model_access: string[];
  agent_access: string[];
  theme_preference?: ThemePreference;
  sidebar_collapsed?: boolean;
  picture_version?: number;
  role?: string;
  is_admin?: boolean;
  permissions?: string[];
  business_unit?: string;
  job_title?:string;
  department?: string;
  office_location?: string;
  manager_id?: string | null;
  manager_name?: string | null;
  is_active?: boolean;
  onboarding_completed?: boolean;
  last_login?: string;
  added_date?: string;
  default_model?: string;
  custom_instructions?: string;
  custom_instructions_enabled?: boolean;
  share_profile_enabled?: boolean;
}

export interface UserSettingsUpdate {
  model_access?: string[];
  agent_access?: string[];
  theme_preference?: ThemePreference;
  sidebar_collapsed?: boolean;
  picture_version?: number;
  // v3 fields
  role?: string;
  is_admin?: boolean;
  permissions?: string[];
  business_unit?: string;
  job_title?:string;
  department?: string;
  office_location?: string;
  manager_id?: string | null;
  is_active?: boolean;
  default_model?: string;
  custom_instructions?: string;
  custom_instructions_enabled?: boolean;
  share_profile_enabled?: boolean;
}

export async function getUserSettings(): Promise<UserSettings> {
  return fetcher<UserSettings>('/api/user-settings');
}

export async function updateUserSettings(userId: string, settings: UserSettingsUpdate): Promise<UserSettings> {
  return fetcher<UserSettings>(`/api/user-settings/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(settings)
  });
}

export async function updateCurrentUserSettings(settings: UserSettingsUpdate): Promise<UserSettings> {
  return fetcher<UserSettings>('/api/user-settings', {
    method: 'PUT',
    body: JSON.stringify(settings)
  });
}

export async function getAllUsers(): Promise<UserSettings[]> {
  return fetcher<UserSettings[]>('/api/user-settings/all');
}

export async function getUserById(userId: string): Promise<UserSettings> {
  return fetcher<UserSettings>(`/api/user-settings/${userId}`);
}

export interface ModelUsageStats {
  rank: number;
  model_key: string;
  model_name: string;
  request_count: number;
}

export interface UserTotalMessages {
  total_messages: number;
  user_messages: number;
  assistant_messages: number;
}

export interface UserTotalCosts {
  total_cost: number;
  total_tokens_input: number;
  total_tokens_output: number;
  total_tokens: number;
  currency: string;
}

export interface MonthlyCostData {
  month: string;
  year: number;
  cost: number;
  tokens_input: number;
  tokens_output: number;
}

export interface PermissionRequestBatch {
  requested_permissions: string[];
  reason?: string;
}

export interface PermissionRequestBatchResponse {
  success: number;
  failed: number;
  created_requests: any[];
  failed_requests: any[];
  message: string;
}

export interface DeleteUserSettingsResponse {
  success: boolean;
  user_id: string;
  message: string;
}

export async function createPermissionRequestBatch(request: PermissionRequestBatch): Promise<PermissionRequestBatchResponse> {
  return fetcher<PermissionRequestBatchResponse>('/api/permission-requests/batch', {
    method: 'POST',
    body: JSON.stringify(request)
  });
}

export async function createUserSettings(userId: string, settings: UserSettingsUpdate): Promise<UserSettings> {
  return fetcher<UserSettings>('/api/user-settings', {
    method: 'POST',
    body: JSON.stringify({ id: userId, ...settings })
  });
}

export async function deleteUserSettings(userId: string): Promise<DeleteUserSettingsResponse> {
  return fetcher<DeleteUserSettingsResponse>(`/api/user-settings/${userId}`, {
    method: 'DELETE'
  });
}

export async function refreshProfilePicture(): Promise<UserSettings> {
  return fetcher<UserSettings>('/api/user-settings/refresh-picture', {
    method: 'POST'
  });
}

export async function completeOnboarding(): Promise<UserSettings> {
  return fetcher<UserSettings>('/api/user-settings/complete-onboarding', {
    method: 'POST'
  });
}

export async function getMyModelsUsage(
  startDate: string,
  endDate: string,
  limit: number = 5
): Promise<ModelUsageStats[]> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    limit: limit.toString()
  });

  return fetcher<ModelUsageStats[]>(`/api/user-settings/my-models-most-used?${params.toString()}`);
}

export async function getMyTotalMessages(
  startDate: string,
  endDate: string
): Promise<UserTotalMessages> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });

  return fetcher<UserTotalMessages>(`/api/user-settings/my-total-messages?${params.toString()}`);
}

export async function getMyTotalCosts(
  startDate: string,
  endDate: string
): Promise<UserTotalCosts> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });

  return fetcher<UserTotalCosts>(`/api/user-settings/my-total-costs?${params.toString()}`);
}

export async function getMyMonthlyCosts(
  numMonths: number = 6
): Promise<MonthlyCostData[]> {
  const params = new URLSearchParams({
    num_months: numMonths.toString()
  });

  return fetcher<MonthlyCostData[]>(`/api/user-settings/my-monthly-costs?${params.toString()}`);
}
